import { useTranslation } from 'next-i18next';
import type { ComponentPropsWithoutRef } from 'react';
import React, { useMemo } from 'react';
import Box from '@mui/material/Box';

// Hooks
import formatDistanceToNow from 'src/utils/formatDistanceToNow';

// Components
import Icon from 'src/components/ui/Icon';
import Typography from 'src/components/ui/Typography';
// Data
import type { AdQuery } from 'src/types/__generated__/graphql';

// Constants
import { colours } from 'src/constants/colours';
import { styledComponent, styledTag } from 'src/utils/styled';

const StyledTypography = styledComponent(Typography)<{ component?: string }>``;
const StyledDescriptionSubHeader = styledTag('aside')`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    margin: 1rem 0;
    gap: 0.75rem;
    `;
type NonNullableAdQuery = Exclude<AdQuery['ad'], undefined | null>;

const AdStats = ({
    isMobile,
    ad,
    dataTestIdPrefix,
    textProps,
    iconProps,
}: {
    isMobile: boolean;
    ad: Pick<NonNullableAdQuery, 'createdAt'> & {
        stats: Pick<NonNullableAdQuery['stats'], 'favoriteCount' | 'viewCount'>;
    };
    dataTestIdPrefix?: string;
    textProps?: Partial<ComponentPropsWithoutRef<typeof Typography>>;
    iconProps?: Partial<ComponentPropsWithoutRef<typeof Icon>>;
}) => {
    const { t, i18n } = useTranslation(['ad', 'common']);
    const prefixWithDash = dataTestIdPrefix ? `${dataTestIdPrefix}-` : '';
    const infoSections: Array<{
        icon: IconProps['name'];
        count: number;
        label: string;
    }> = useMemo(
        () => [
            { icon: 'Show', count: ad.stats.viewCount, label: 'views' },
            { icon: 'Bookmark', count: ad.stats.favoriteCount, label: 'favorites' },
        ],
        [ad.stats],
    );

    return (
        <StyledDescriptionSubHeader>
            <Box display="flex" flexDirection="row" alignItems="center">
                <Typography
                    data-target={`${prefixWithDash}ad-published-since`}
                    data-target-id={`${prefixWithDash}ad-published-since`}
                    sx={{ fontSize: '0.875rem' }}
                    {...textProps}
                >
                    {t('ad:since', {
                        momentString: formatDistanceToNow(ad.createdAt as string, i18n.language as SupportedLangs),
                    })}
                </Typography>
            </Box>
            {infoSections.map(({ label, count, icon }) => (
                <Box key={label} display="flex" alignItems="center" gap="0.25rem">
                    <Box alignItems="center">
                        <Icon color={colours.black.O72} size="1rem" name={icon} {...iconProps} />
                    </Box>
                    <StyledTypography
                        component="span"
                        data-target-id={`${prefixWithDash}${label}-count`}
                        sx={{ fontSize: '0.875rem' }}
                        {...textProps}
                    >
                        {count}
                        {!isMobile && ` ${t(`ad:${label}`)}`}
                    </StyledTypography>
                </Box>
            ))}
        </StyledDescriptionSubHeader>
    );
};

export default AdStats;
